/**
 * CREATE / ALTER / DROP TRIGGER structure extractor — the grammar-backed
 * replacement for the removed regex parked-trigger parser (VDE-2 Slice 3).
 * Pure, regex-free: every structural decision comes from character-level
 * keyword / phrase consumption plus the bespoke `trigger.parse.ts` helpers
 * (`readTriggerName`/`readAnyIdent`, `findWordBounded`/`findPhrase`, the
 * `captureExecuteFunction` paren-backtrack) — never regex (project rule
 * VQE-D10), INCLUDING never regex into the trigger body. It deliberately does
 * NOT use the shared `readIdentifier`/`findMatchingParen` primitives: those
 * are comment- and engine-aware and would change behaviour vs the removed
 * parser, breaking the required byte-parity. Low-level lexical helpers live in
 * `trigger.parse.ts`; this module is the clause orchestration + entry points.
 *
 * TRIGGER is **seed-only** AND the first **body-bearing** shell: the inline
 * `[Edit Trigger]` popover builds SQL from form values and replaces the
 * statement text. There is no structural apply path, so the arm carries the
 * pre-parsed seed (`parsed`) plus the statement range. The procedural body is
 * carried as ONE OPAQUE verbatim string (`bodyText`) — never parsed, never
 * regex'd into (it may contain nested `BEGIN…END`, quoted strings, comments
 * and `;`, all opaque).
 *
 * Captured shape (best-effort, engine-spanning):
 *   CREATE [OR REPLACE | OR ALTER] [EDITIONABLE|NONEDITIONABLE] [TEMP|TEMPORARY]
 *          TRIGGER [schema.]name
 *          { BEFORE|AFTER|INSTEAD OF <evt[,…]> ON tbl | ON tbl AFTER <evt[,…]> }
 *          [FOR EACH ROW|STATEMENT] [WHEN (…)]
 *          { AS <tsql> | EXECUTE FUNCTION|PROCEDURE fn(args) | <body> }
 *   ALTER TRIGGER name <…>   /   DROP TRIGGER name
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D6 — Trigger shell UI; now grammar-
 * backed via DSE2 (VDE-2 Slice 3).
 */

import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import type { DdlStructure, ParsedBuffer } from '../../types';

import type { ParsedParkedTrigger } from './triggerTypes';
import {
  captureExecuteFunction,
  consumePhrase,
  consumeWord,
  findPhrase,
  findWordBounded,
  readEventList,
  readTriggerName,
  skipSpacesLocal,
} from './trigger.parse';

export type { ParsedParkedTrigger } from './triggerTypes';

/** Default options for the test/string entry shell — sqlite accepts all
 *  three quoted-identifier styles (`[ ]`, backticks, `" "`), so an engine-
 *  agnostic parked statement parses regardless of source dialect. */
const STRING_SHELL_OPTIONS: ParseOptions = { engine: 'sqlite' };

/** Trim ASCII whitespace off both ends of `[start, end)`, mirroring the
 *  removed parser's leading `stmtText.trim()` (all offsets it computed were
 *  relative to the trimmed string). */
function trimRange(source: string, start: number, end: number): { s: number; e: number } {
  let s = start;
  let e = end;
  while (s < e && (source[s] === ' ' || source[s] === '\t' || source[s] === '\n'
    || source[s] === '\r' || source[s] === '\f' || source[s] === '\v')) s++;
  while (e > s && (source[e - 1] === ' ' || source[e - 1] === '\t' || source[e - 1] === '\n'
    || source[e - 1] === '\r' || source[e - 1] === '\f' || source[e - 1] === '\v')) e--;
  return { s, e };
}

interface PrefixState {
  cursor: number;
  orReplace: boolean;
  mssqlOrAlter: boolean;
  oracleEditionable: boolean;
  oracleNonEditionable: boolean;
  sqliteTemporary: boolean;
}

/** CREATE-only prefix consumption (`OR REPLACE` / `OR ALTER` / `EDITIONABLE`
 *  / `NONEDITIONABLE` / `TEMP|TEMPORARY`). Bounded 8-iteration loop, faithful
 *  to the removed parser's `consumePrefixes`. */
function consumePrefixes(source: string, start: number, end: number): PrefixState {
  let cursor = start;
  let orReplace = false;
  let mssqlOrAlter = false;
  let oracleEditionable = false;
  let oracleNonEditionable = false;
  let sqliteTemporary = false;
  for (let iter = 0; iter < 8; iter++) {
    cursor = skipSpacesLocal(source, cursor, end);
    const orRep = consumePhrase(source, cursor, end, ['OR', 'REPLACE']);
    if (orRep !== -1) { orReplace = true; cursor = orRep; continue; }
    const orAlt = consumePhrase(source, cursor, end, ['OR', 'ALTER']);
    if (orAlt !== -1) { mssqlOrAlter = true; cursor = orAlt; continue; }
    const ed = consumeWord(source, cursor, end, 'EDITIONABLE');
    if (ed !== -1) { oracleEditionable = true; cursor = ed; continue; }
    const ned = consumeWord(source, cursor, end, 'NONEDITIONABLE');
    if (ned !== -1) { oracleNonEditionable = true; cursor = ned; continue; }
    const tmp = consumeWord(source, cursor, end, 'TEMP') !== -1
      ? consumeWord(source, cursor, end, 'TEMP')
      : consumeWord(source, cursor, end, 'TEMPORARY');
    if (tmp !== -1) { sqliteTemporary = true; cursor = tmp; continue; }
    break;
  }
  return { cursor, orReplace, mssqlOrAlter, oracleEditionable, oracleNonEditionable, sqliteTemporary };
}

/** True when the trimmed range begins with the (prefix, NOT word-bounded)
 *  keyword `kw` — faithful to the removed parser's `upper.startsWith(...)`. */
function startsWithKeyword(source: string, s: number, e: number, kw: string): boolean {
  if (s + kw.length > e) return false;
  return source.slice(s, s + kw.length).toUpperCase() === kw;
}

/** Shared pure core. Operates on a byte range; returns the seed contract or
 *  null for non-trigger / structurally-incomplete input. Mirrors the removed
 *  parser's `parseParkedTriggerStatement` + `parseCreateBody` exactly.
 *
 *  No `ParseOptions` arg — unlike the GRANT/TYPE cores this scan is genuinely
 *  engine-agnostic: the removed regex parser accepted every quote style
 *  (double-quote, backtick, bracket) and comments are never special here, so
 *  the seed is identical regardless of source dialect. The public entry
 *  functions still take `options` for contract parity with the siblings. */
function parseTriggerCore(
  source: string,
  rangeStart: number,
  rangeEnd: number,
): ParsedParkedTrigger | null {
  const { s: start, e: end } = trimRange(source, rangeStart, rangeEnd);

  let action: 'create' | 'alter' | 'drop';
  let cursor: number;
  if (startsWithKeyword(source, start, end, 'CREATE')) { action = 'create'; cursor = start + 'CREATE'.length; }
  else if (startsWithKeyword(source, start, end, 'ALTER')) { action = 'alter'; cursor = start + 'ALTER'.length; }
  else if (startsWithKeyword(source, start, end, 'DROP')) { action = 'drop'; cursor = start + 'DROP'.length; }
  else return null;

  let prefix: PrefixState = {
    cursor,
    orReplace: false,
    mssqlOrAlter: false,
    oracleEditionable: false,
    oracleNonEditionable: false,
    sqliteTemporary: false,
  };
  if (action === 'create') prefix = consumePrefixes(source, cursor, end);
  cursor = skipSpacesLocal(source, prefix.cursor, end);

  const afterTrigger = consumeWord(source, cursor, end, 'TRIGGER');
  if (afterTrigger === -1) return null;
  cursor = skipSpacesLocal(source, afterTrigger, end);

  const nameRead = readTriggerName(source, cursor, end);
  if (!nameRead) return null;
  const name = nameRead.name;
  cursor = nameRead.end;

  if (action !== 'create') {
    return {
      action,
      name,
      ...(prefix.orReplace ? { orReplace: true } : {}),
      ...(prefix.mssqlOrAlter ? { mssqlOrAlter: true } : {}),
    };
  }

  return parseCreateBody(source, cursor, end, name, prefix);
}

/** Parse the CREATE-trigger tail (timing / target / events / granularity /
 *  body). `restStart` is the index just past the trigger name within the
 *  trimmed range; mirrors the removed parser's `parseCreateBody(name, rest,…)`
 *  where `rest = trimmed.slice(cursor)`. */
function parseCreateBody(
  source: string,
  restStart: number,
  end: number,
  name: ParsedParkedTrigger['name'],
  prefix: PrefixState,
): ParsedParkedTrigger {
  let timing: 'before' | 'after' | 'instead-of' | undefined;
  // `consumed` tracks an absolute index into [restStart, end); the removed
  // parser tracked it relative to `rest` (its `tail = rest.slice(consumed)`).
  let consumed = restStart;
  const ws = skipSpacesLocal(source, restStart, end);
  const before = consumeWord(source, ws, end, 'BEFORE');
  const after = before === -1 ? consumeWord(source, ws, end, 'AFTER') : -1;
  if (before !== -1) { timing = 'before'; consumed = before; }
  else if (after !== -1) { timing = 'after'; consumed = after; }
  else {
    // `/^\s*INSTEAD\s+OF\b/i.test(rest)` — tests `rest` (with leading ws).
    const lead = skipSpacesLocal(source, restStart, end);
    const io = consumePhrase(source, lead, end, ['INSTEAD', 'OF']);
    if (io !== -1) { timing = 'instead-of'; consumed = io; }
  }

  let target: ParsedParkedTrigger['target'];
  let events: ReadonlyArray<'insert' | 'update' | 'delete'> | undefined;
  if (timing) {
    const ev = readEventList(source, consumed, end);
    events = ev.events;
    consumed = ev.consumed;
  } else {
    const onAtWs = consumeWord(source, ws, end, 'ON');
    if (onAtWs !== -1) {
      // mssql shape: ON <target> before timing.
      let i = skipSpacesLocal(source, onAtWs, end);
      const tm = readTriggerName(source, i, end);
      if (tm) { target = tm.name; i = tm.end; }
      const ws2 = skipSpacesLocal(source, i, end);
      const after2 = consumeWord(source, ws2, end, 'AFTER');
      if (after2 !== -1) { timing = 'after'; consumed = after2; }
      else {
        // `/^\s*INSTEAD\s+OF\b/i.test(rest.slice(ws2))` — tests from ws2.
        const io2 = consumePhrase(source, ws2, end, ['INSTEAD', 'OF']);
        if (io2 !== -1) { timing = 'instead-of'; consumed = io2; }
      }
      if (timing) {
        const ev = readEventList(source, consumed, end);
        events = ev.events;
        consumed = ev.consumed;
      }
    }
  }

  if (!target) {
    const ws2 = skipSpacesLocal(source, consumed, end);
    const onAtWs2 = consumeWord(source, ws2, end, 'ON');
    if (onAtWs2 !== -1) {
      let i = skipSpacesLocal(source, onAtWs2, end);
      const tm = readTriggerName(source, i, end);
      if (tm) { target = tm.name; i = tm.end; }
      consumed = i;
    }
  }

  // `tail = rest.slice(consumed)` — here the absolute span [consumed, end).
  const tailStart = consumed;
  let granularity: 'row' | 'statement' | undefined;
  let pgsqlExecuteFunction: string | undefined;
  let bodyText: string | undefined;

  const forEachRow = findPhrase(source, tailStart, end, ['FOR', 'EACH', 'ROW']);
  const forEachStmt = findPhrase(source, tailStart, end, ['FOR', 'EACH', 'STATEMENT']);
  if (forEachRow) granularity = 'row';
  else if (forEachStmt) granularity = 'statement';

  const execFn = captureExecuteFunction(source, tailStart, end);
  if (execFn) pgsqlExecuteFunction = execFn;

  // Body precedence: `AS <tsql>` first (anywhere in tail, word-bounded +
  // trailing-ws), else after `FOR EACH ROW` (skipping a leading `WHEN (…)`),
  // else after `FOR EACH STATEMENT`. Body is the OPAQUE trimmed remainder —
  // never parsed.
  const asAt = findWordBounded(source, tailStart, end, 'AS', true);
  if (asAt !== -1) {
    const bodyStart = skipSpacesLocal(source, asAt + 'AS'.length, end);
    bodyText = source.slice(bodyStart, end).trim();
  } else if (forEachRow) {
    let body = source.slice(forEachRow.end, end).trim();
    if (body.toUpperCase().startsWith('WHEN ')) {
      // Skip past the `WHEN (…)` clause's first `)` — mirrors the removed
      // parser's `body.indexOf(')')` (single-level, NOT paren-balanced).
      const close = body.indexOf(')');
      if (close !== -1) body = body.slice(close + 1).trim();
    }
    bodyText = body;
  } else if (forEachStmt) {
    bodyText = source.slice(forEachStmt.end, end).trim();
  } else if (!pgsqlExecuteFunction) {
    // No `AS` / `FOR EACH ROW|STATEMENT` anchor — SQLite triggers and Oracle
    // statement-level triggers place the body (`BEGIN … END`, or a single
    // statement) directly after the `ON <target>` header. Carry the opaque
    // remainder verbatim (never parsed), skipping a leading parenthesised
    // `WHEN (…)` guard the same single-level way as the FOR EACH ROW branch.
    let body = source.slice(tailStart, end).trim();
    if (body.toUpperCase().startsWith('WHEN ')) {
      const close = body.indexOf(')');
      if (close !== -1) body = body.slice(close + 1).trim();
    }
    bodyText = body;
  }

  return {
    action: 'create',
    name,
    ...(target ? { target } : {}),
    ...(timing ? { timing } : {}),
    ...(events && events.length > 0 ? { events } : {}),
    ...(bodyText && bodyText.length > 0 && !pgsqlExecuteFunction ? { bodyText } : {}),
    ...(prefix.orReplace ? { orReplace: true } : {}),
    ...(prefix.mssqlOrAlter ? { mssqlOrAlter: true } : {}),
    ...(granularity ? { granularity } : {}),
    ...(pgsqlExecuteFunction ? { pgsqlExecuteFunction } : {}),
    ...(prefix.oracleEditionable ? { oracleEditionable: true } : {}),
    ...(prefix.oracleNonEditionable ? { oracleNonEditionable: true } : {}),
    ...(prefix.sqliteTemporary ? { sqliteTemporary: true } : {}),
  };
}

/** Buffer-entry seed helper consumed by the inline `[Edit Trigger]` popover.
 *  Returns the parked-trigger seed or null on non-trigger / malformed. */
export function extractParkedTrigger(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): ParsedParkedTrigger | null {
  const { source } = buffer.parsedDocument;
  return parseTriggerCore(
    source,
    statementInfo.range.startOffset,
    statementInfo.range.endOffset,
  );
}

/** Thin string-entry shell (tests + engine-agnostic callers). The `options`
 *  param is accepted for signature parity with the GRANT/TYPE string shells
 *  (callers may pass an explicit engine) but the trigger scan is engine-
 *  agnostic, so it does not affect the produced seed. */
export function extractParkedTriggerFromString(
  stmtText: string,
  options: ParseOptions = STRING_SHELL_OPTIONS,
): ParsedParkedTrigger | null {
  void options;
  return parseTriggerCore(stmtText, 0, stmtText.length);
}

/** DSE2 arm — `extractDdlStructure` dispatches CREATE / ALTER / DROP TRIGGER
 *  here. Action-discriminated kind (mirrors `extractGrant`): `'alter'` →
 *  `alter-trigger`, `'drop'` → `drop-trigger`, else (`'create'` or
 *  `parsed === null`) → `create-trigger`. Never throws. */
export function extractTrigger(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): DdlStructure {
  const parsed = extractParkedTrigger(buffer, statementInfo);
  const kind =
    parsed?.action === 'alter'
      ? 'alter-trigger'
      : parsed?.action === 'drop'
        ? 'drop-trigger'
        : 'create-trigger';
  return { kind, range: statementInfo.range, parsed };
}
